import React, { useState, useEffect } from 'react';
import StudioBanner from '../../components/studio/StudioBanner';
import { useParams, useNavigate } from 'react-router-dom';
import studioApi from '../../api/studioClient';
const api = studioApi;
import toast from 'react-hot-toast';
import { Plus, Eye, Save, RotateCcw } from 'lucide-react';
import AdminPageHeader from '../../components/studio/AdminPageHeader';

export default function TitleBuilder() {
  const { recordTypeId } = useParams();
  const navigate = useNavigate();
  const [fields, setFields] = useState([]);
  const [formula, setFormula] = useState('');
  const [allowManual, setAllowManual] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState('');
  const [recordTypeName, setRecordTypeName] = useState('');
  const [linkedFieldGroups, setLinkedFieldGroups] = useState([]); // [{linkField, targetTypeName, fields}]

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/template-studio/${recordTypeId}/title-config`);
        setFields(res.data.fields || []);
        setFormula(res.data.title_config?.formula || '');
        setAllowManual(res.data.title_config?.allow_manual !== false);
        // Get record type name
        const rtRes = await api.get(`/template-studio/${recordTypeId}`);
        setRecordTypeName(rtRes.data.recordType?.name || rtRes.data.name || '');
        // Fetch fields from linked record types (RECORD_LINK fields)
        const linkFields = (res.data.fields || []).filter(f => f.field_type === 'RECORD_LINK' && f.config?.target_record_type_id);
        const groups = [];
        for (const lf of linkFields) {
          try {
            const linkedFieldsRes = await api.get(`/admin/fields?record_type_id=${lf.config.target_record_type_id}`);
            const linkedFields = (linkedFieldsRes.data?.fields || []).filter(f => !f.deleted_at && ['TEXT','DATE','DROPDOWN','NUMBER','PHONE','EMAIL','CURRENCY'].includes(f.field_type));
            groups.push({
              linkField: lf,
              targetTypeName: lf.config.target_type_name || lf.label,
              fields: linkedFields,
            });
          } catch(e) {}
        }
        setLinkedFieldGroups(groups);
      } catch (err) {
        toast.error('Failed to load title config');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [recordTypeId]);

  // Live preview
  useEffect(() => {
    if (!formula.trim()) {
      setPreview('');
      return;
    }
    let result = formula;
    // Resolve smart tokens
    result = result.replace(/\{client\.first_name\}/g, 'John');
    result = result.replace(/\{client\.last_name\}/g, 'Smith');
    result = result.replace(/\{reference_number\}/g, 'SMI-1007');
    // Resolve linked field previews
    (linkedFieldGroups || []).forEach(function(group) {
      (group.fields || []).forEach(function(f) {
        var sampleMap = { TEXT: f.name.includes('first') ? 'John' : f.name.includes('last') ? 'Smith' : f.label, DATE: '2026-03-15', NUMBER: '42', CURRENCY: '15000.00', EMAIL: 'john@example.com', PHONE: '(555) 123-4567', DROPDOWN: 'Option A' };
        var sample = sampleMap[f.field_type] || f.label;
        var escaped = (group.linkField.name + '.' + f.name).replace(/[.*+?^${}()|[\]]/g, '\\    fields.forEach(f => {');
        result = result.replace(new RegExp('\\{' + escaped + '\\}', 'g'), sample);
      });
    });
    fields.forEach(f => {
      const sampleValues = {
        TEXT: f.label === 'First Name' || f.name.includes('first') ? 'John' :
              f.label === 'Last Name' || f.name.includes('last') ? 'Smith' :
              f.label,
        DATE: '2026-03-15',
        DATETIME: '2026-03-15 10:30',
        NUMBER: '42',
        CURRENCY: '15000.00',
        EMAIL: 'john@example.com',
        PHONE: '(555) 123-4567',
        DROPDOWN: 'Option A',
      };
      const sample = sampleValues[f.field_type] || f.label;
      const nameRegex = new RegExp('\\{' + f.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\}', 'g');
      result = result.replace(nameRegex, sample);
    });
    result = result.replace(/\s+/g, ' ').trim();
    result = result.replace(/^[\s\-,|/]+|[\s\-,|/]+$/g, '').trim();
    setPreview(result);
  }, [formula, fields]);

  const insertField = (field, linkPrefix) => {
    const token = linkPrefix ? `{${linkPrefix}.${field.name}}` : `{${field.name}}`;
    setFormula(prev => prev + token);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/template-studio/${recordTypeId}/title-config`, {
        formula,
        allow_manual: allowManual,
      });
      const updated = res.data.records_updated || 0;
      if (updated > 0) {
        toast.success(`Saved — ${updated} existing records updated`);
      } else {
        toast.success('Title formula saved');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setFormula('');
  };

  if (loading) return <div className="text-center py-12 text-gray-400">Loading...</div>;

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <StudioBanner />
      <AdminPageHeader title="Record Title Builder" description="Define how record titles are generated" backTo="/template-studio" />

      {/* Formula Editor */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
        <label className="block text-sm font-semibold text-gray-700 mb-1">Title Formula</label>
        <p className="text-xs text-gray-400 mb-3">
          Type text and insert field tokens. Example: <span className="font-mono bg-gray-100 px-1 rounded">{'{client.first_name}'} {'{client.last_name}'} - {'{date_of_loss}'}</span>
        </p>
        <div className="relative">
          <textarea
            value={formula}
            onChange={e => setFormula(e.target.value)}
            rows={3}
            placeholder="e.g., {first_name} {last_name} - {date_of_loss}"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-400"
          />
          {formula && (
            <button onClick={handleClear}
              className="absolute top-2 right-2 p-1 text-gray-300 hover:text-gray-500"
              title="Clear formula">
              <RotateCcw className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Available Fields */}
        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-500 mb-2">Click to insert tokens:</label>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {/* Smart tokens - always available */}
            {[
              { token: '{client.first_name}', label: 'Client First Name', color: 'purple' },
              { token: '{client.last_name}', label: 'Client Last Name', color: 'purple' },
              { token: '{reference_number}', label: 'Reference Number', color: 'blue', isSystem: true },
            ].map(t => (
              <button key={t.token} onClick={() => setFormula(prev => prev + t.token)}
                className={`flex items-center gap-1 px-2.5 py-1.5 border rounded-lg text-xs hover:opacity-80 transition-colors ${
                  t.color === 'purple' ? 'bg-purple-50 border-purple-200 text-purple-700' :
                  t.color === 'blue' ? 'bg-blue-50 border-blue-200 text-blue-700' :
                  'bg-gray-50 border-gray-200 text-gray-700'
                }`}
                >
                <Plus className="h-3 w-3" />
                <span className="font-medium">{t.label}</span>
              </button>
            ))}
          </div>
          <label className="block text-xs font-medium text-gray-400 mb-2">Record fields:</label>
          <div className="flex flex-wrap gap-1.5">
            {fields.filter(f => ['DATE','DROPDOWN','TEXT'].includes(f.field_type) && !f.name.includes('client_') && !['case_number'].includes(f.name)).slice(0, 12).map(f => (
              <button key={f.id} onClick={() => insertField(f)}
                className="flex items-center gap-1 px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs hover:bg-gray-100 transition-colors">
                <Plus className="h-3 w-3" />
                <span className="font-medium">{f.label}</span>
              </button>
            ))}
          </div>
        </div>
        {/* Preview */}
        {formula.trim() && (
          <div className="mt-4 bg-brand-50 border border-brand-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Eye className="h-4 w-4 text-brand-500" />
              <span className="text-xs font-semibold text-brand-600">Live Preview</span>
            </div>
            <p className="text-sm font-medium text-gray-900">
              {preview || <span className="text-gray-400 italic">Empty — fields have no sample data</span>}
            </p>
            <p className="text-[10px] text-brand-400 mt-1">Preview uses sample data. Actual titles use real field values.</p>
          </div>
        )}
      </div>

      {/* Options */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Options</h3>
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={allowManual}
            onChange={e => setAllowManual(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-brand-600 mt-0.5" />
          <div>
            <span className="text-sm font-medium text-gray-700">Allow manual title override</span>
            <p className="text-xs text-gray-400 mt-0.5">
              When enabled, users can edit the title manually. When disabled, the title is always auto-generated from the formula.
            </p>
          </div>
        </label>
      </div>

      {/* Info */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
        <p className="text-xs text-amber-700">
          <strong>Note:</strong> Saving will immediately update all existing records of this type with the new title formula.
          Titles are also recalculated every time a record is created or updated.
        </p>
      </div>

      {/* Save */}
      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors">
          <Save className="h-4 w-4" />
          {saving ? 'Saving...' : 'Save & Apply to All Records'}
        </button>
      </div>
    </div>
  );
}
